import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useCartStore } from '../stores/cart';
import { cartActions } from '../services/cartService';
import { useProductStockStore } from '@/features/Product/stores/productStock';
import {
  allocateCartStock,
  orderByStockPriority,
} from '@/features/Product/services/productAvailability';
import type { CartItemQuantity } from '../types/models';
import type { ShopifyCart, ShopifyCartItem } from '../types/servicesShopifyCart';

type CartStockAdjustmentOptions = {
  unavailableLineKeys: string[];
  protectedLineIdentities: string[];
  isFreeGiftItem: (item: ShopifyCartItem) => boolean;
  getExactStock: (variantId: number) => number | undefined;
};

export const getCartStockAdjustments = (
  cart: ShopifyCart,
  {
    unavailableLineKeys,
    protectedLineIdentities,
    isFreeGiftItem,
    getExactStock,
  }: CartStockAdjustmentOptions,
): CartItemQuantity[] => {
  const isUnavailable = ({ key }: ShopifyCartItem) => unavailableLineKeys.includes(key);
  const isRepairable = (item: ShopifyCartItem) => !isFreeGiftItem(item);
  const removals = cart.items
    .filter((item) => isUnavailable(item) && isRepairable(item))
    .map(({ key }) => ({ key, quantity: 0 }));
  const lines = orderByStockPriority(
    cart.items.filter((item) => !isUnavailable(item)),
    new Set(protectedLineIdentities),
  );
  const allocations = allocateCartStock(lines, getExactStock);
  const reductions = lines.filter(isRepairable).flatMap(({ key, quantity }) => {
    const allocatedQuantity = allocations.get(key) ?? quantity;
    return allocatedQuantity < quantity ? [{ key, quantity: allocatedQuantity }] : [];
  });
  return [...removals, ...reductions];
};

export const useCartStockValidation = () => {
  const cartStore = useCartStore();
  const { isFreeGiftItem } = cartStore;
  const { cartData, cartDataLoaded, cartUpdateInProgress, latestAddLineIdentities } =
    storeToRefs(cartStore);
  const productStockStore = useProductStockStore();
  const { refreshStock, getVariantStock } = productStockStore;
  const { variantStock, unavailableLines } = storeToRefs(productStockStore);
  let isAdjusting = false;
  let hasPendingCheck = false;
  let attemptedSignature: string | null = null;

  const getExactStock = (variantId: number): number | undefined => {
    const stock = getVariantStock(variantId);
    return stock?.isExact ? stock.quantity : undefined;
  };

  const getStockAdjustments = (cart: ShopifyCart): CartItemQuantity[] =>
    getCartStockAdjustments(cart, {
      unavailableLineKeys: unavailableLines.value,
      protectedLineIdentities: latestAddLineIdentities.value,
      isFreeGiftItem,
      getExactStock,
    });

  const stockAdjustments = computed(() => getStockAdjustments(cartData.value));

  const getSignature = () =>
    JSON.stringify([
      cartData.value.items.map(({ key, quantity }) => [key, quantity]),
      variantStock.value,
      unavailableLines.value,
    ]);

  const applyStockAdjustments = async () => {
    if (isAdjusting) {
      hasPendingCheck = true;
      return;
    }
    if (!cartDataLoaded.value || cartUpdateInProgress.value) return;
    if (!stockAdjustments.value.length || getSignature() === attemptedSignature) return;
    isAdjusting = true;
    try {
      await refreshStock({ force: true });
      if (!stockAdjustments.value.length) return;
      attemptedSignature = getSignature();
      await cartActions().updateCartItemQuantities(getStockAdjustments, {
        isStockCorrection: true,
      });
    } finally {
      isAdjusting = false;
      if (hasPendingCheck) {
        hasPendingCheck = false;
        applyStockAdjustments();
      }
    }
  };

  watch([stockAdjustments, cartUpdateInProgress], applyStockAdjustments);
};
